<template>
  <m-step-layout
    title="Review"
    description="Please review the information provided"
    type="sm"
  >
    <m-form>
      <fund-information
        :fund="fund"
        :canEdit="canEdit && !isComplete"
      ></fund-information>
      <m-divider />
      <review-item
        title="Payment Instruction Summary"
        @edit="$emit('step-change', 0)"
        :isComplete="isComplete"
      >
        <!-- eslint-disable-next-line max-len -->
        <p>Payment Instructions for financial year ending 30 June <span class="font-bold">{{ getItem.finYear }}</span>.</p>
        <h3 class="mt-2 font-bold">Documentation relates to payments for:</h3>
        <p class="text-sm">{{getItem.ratify.label}}</p>

        <h3 class="mt-2 font-bold">Members for whom payment instructions are provided</h3>
        <div v-for="member in members" :key="member.id">
          <p>{{ member.name }}</p>
        </div>
      </review-item>
      <review-item
        title="Member Accounts"
        @edit="$emit('step-change', 0)"
        :isComplete="isComplete"
      >
        <div v-for="member in members" :key="member.id">
          <p class="font-bold">{{ member.name }}</p>
          <ul class="text-sm">
            <li
              v-for="account in accounts(member.id)"
              :key="account.identifier"
            >
              {{ account.label }} ({{ account.identifier }} /
              {{ account.taxFree | percent }} tax free)
            </li>
          </ul>
        </div>
      </review-item>
      <review-item
        title="Payment Instructions"
        @edit="$emit('step-change', 1)"
        :isComplete="isComplete"
      >
        <p>Execute payments as follows:</p>
        <ul class="text-sm list-outside ml-4 list-disc">
          <li v-for="(rule, idx) in rules" :key="idx">
            {{ processRule(rule) }}
            <div v-if="showAccounts(rule)"
              class="col-span-10 text-sm p-4 border border-gray-200 mt-2"
            >
              <p v-for="(account, idx) in renderAccount(rule)" :key="idx" v-html="account" />
            </div>
          </li>
        </ul>
      </review-item>
      <div>
        <pdf-container />
      </div>
    </m-form>
    <div class="p-4 bg-gray-300 flex justify-end">
      <div class="relative w-32">
        <m-button
          @click="handleSaveAndExit"
          class="w-full"
          data-test="save-btn-exit"
          >Save &amp; Exit</m-button
        >
        <div
          class="absolute inset-0 flex items-center justify-center bg-blue-800 rounded"
          v-if="loadingExit"
        >
          <div class="h-4 w-4">
            <m-loader class="text-white"></m-loader>
          </div>
        </div>
      </div>
      <div class="ml-4 relative w-48" v-if="!isComplete">
        <m-button
          @click="showConfirmModal = true"
          class="w-full"
          data-test="save-btn-complete"
          >Save &amp; Complete</m-button
        >
        <div
          class="absolute inset-0 flex items-center justify-center bg-blue-800 rounded"
          v-if="loadingComplete"
        >
          <div class="h-4 w-4">
            <m-loader class="text-white"></m-loader>
          </div>
        </div>
      </div>
      <confirm-modal
        :show="showConfirmModal"
        @close="showConfirmModal = false"
        @confirm="handleSaveAndNew"
      ></confirm-modal>
    </div>
  </m-step-layout>
</template>

<script>
import _ from 'lodash';
import { pensionInstructionMixin } from '@/app/mixins/toolkit/pension-instructions/instruction';
import FundInformation from '@/app/modules/fund/components/fund-information/fund-information.vue';
import ReviewItem from '@/app/modules/toolkit/common/components/review-item/review-item.vue';
import ConfirmModal from '@/app/modules/toolkit/common/components/confirm-modal/confirm-modal.vue';
import PdfContainer from '@/app/modules/pdf/containers/pdf-container.vue';
import { types, percent } from '@/app/filters/filters';

export default {
  name: 'review',
  mixins: [pensionInstructionMixin],
  components: {
    FundInformation,
    ReviewItem,
    ConfirmModal,
    PdfContainer,
  },
  data: () => ({
    loadingComplete: false,
    loadingExit: false,
    showConfirmModal: false,
  }),
  computed: {
    canEdit() {
      const canEditFund = this.$store.getters['fund/canEditActiveFund'] || false;
      const appPermissions = this.$store.getters['account/getAppPermissions'] || [];
      const toolkitAppPermission = appPermissions.filter(app => app.appName === 'TOOLKIT')[0];
      return toolkitAppPermission.permission === 'OWNER' && canEditFund;
    },
    isComplete() {
      return this.$store.getters['toolkit/getItem'].status === 'COMPLETE';
    },
    fund() {
      return this.$store.getters['fund/getFund'];
    },
    item() {
      return this.$store.getters['toolkit/getItem'];
    },
    members() {
      const { members } = this.getItem;
      return Object.keys(members).map(key => ({
        id: key,
        name: members[key].name,
      }));
    },
    accounts() {
      const { members } = this.getItem;
      return (id) => {
        const { accounts } = members[id];
        return _.values(accounts);
      };
    },
    rules() {
      const { rules } = this.getItem;
      return rules;
    },
  },
  methods: {
    isComputedRule(rule) {
      return (typeof rule !== 'string');
    },
    processRule(rule) {
      if (this.isComputedRule(rule)) {
        return this.processedPDF(rule);
      }
      return rule;
    },
    renderAccount(rule) {
      const output = [];

      rule.accounts.ruleAccounts.forEach((account) => {
        let row = '';
        if (rule.extra.type === 'percentage') {
          row = `<span class="font-bold">${account.value}%</span> - `;
        }
        row += `${account.member.name} / ${account.label} / ${account.identifier} / ${percent(account.taxFree)} tax free`;
        output.push(row);
      });
      return output;
    },
    saveItem(complete) {
      return this.$store.dispatch('toolkit/saveItem', complete);
    },
    handleSaveAndNew() {
      this.loadingComplete = true;
      this.showConfirmModal = false;
      this.saveItem(true)
        .then(() => {
          this.$mixpanel.track('Toolkit Item', {
            uuid: this.item.uuid,
            action: 'Completed',
            status: types(this.item.status),
            type: this.item.type,
          });

          if (this.canEdit) {
            this.$router.push('/toolkit/pension/payment-instruction');
          } else {
            this.$router.push('/');
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
    handleSaveAndExit() {
      this.loadingExit = true;
      this.$mixpanel.track('Toolkit Item', {
        uuid: this.item.uuid,
        action: 'Exit',
        status: types(this.item.status),
        type: this.item.type,
      });
      if (this.canEdit) {
        this.$router.push('/toolkit/pension/payment-instruction');
      } else {
        this.$router.push('/');
      }
    },
  },
};
</script>
